import { useState, useEffect } from 'react';
import { useAuth } from '@/App';
import { useNavigate } from 'react-router-dom';
import apiClient from '@/utils/apiClient';
import { Users, UserCheck, BookOpen, Activity, Upload, Settings, UserPlus } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';

const AdminHome = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalStudents: 0,
    activeStudents: 0,
    inactiveStudents: 0,
    totalWardens: 0,
    classSummary: []
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      // Fetch students
      const studentsResponse = await apiClient.get('/v1/admin/students/', {
        params: { hostel_id: user.hostel_id }
      });
      const students = Array.isArray(studentsResponse.data) ? studentsResponse.data : [];

      // Fetch wardens
      const wardensResponse = await apiClient.get('/v1/admin/wardens/', {
        params: { hostel_id: user.hostel_id }
      });
      const wardens = Array.isArray(wardensResponse.data) ? wardensResponse.data : [];

      // Calculate stats
      const activeStudents = students.filter(s => !s.is_inactive).length;
      const inactiveStudents = students.filter(s => s.is_inactive).length;

      // Group by class
      const classMap = {};
      students.forEach(student => {
        const className = student.class_name || 'Unassigned';
        if (!classMap[className]) {
          classMap[className] = 0;
        }
        classMap[className]++;
      });

      const classSummary = Object.entries(classMap)
        .map(([name, count]) => ({ name, count }))
        .sort((a, b) => b.count - a.count);

      setStats({
        totalStudents: students.length,
        activeStudents,
        inactiveStudents,
        totalWardens: wardens.length,
        classSummary
      });
    } catch (error) {
      toast.error('Failed to load dashboard statistics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div data-testid="admin-home" className="space-y-6">
      <div className="animate-fade-in">
        <h1 className="text-3xl font-bold mb-2" style={{ color: '#2C5F7F' }}>
          Welcome, {user?.name}!
        </h1>
        <p className="text-gray-600">System Administrator Dashboard</p>
      </div>

      {/* Main Statistics Cards */}
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Total Students */}
        <Card className="glass-effect card-hover" data-testid="total-students-card">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Students</CardTitle>
            <Users className="h-5 w-5" style={{ color: '#5A9FD4' }} />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold" style={{ color: '#2C5F7F' }}>
              {stats.totalStudents}
            </div>
            <p className="text-xs text-gray-600 mt-1">
              In this hostel
            </p>
          </CardContent>
        </Card>

        {/* Active/Inactive Students */}
        <Card className="glass-effect card-hover" data-testid="active-students-card">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Student Status</CardTitle>
            <Activity className="h-5 w-5" style={{ color: '#5A9FD4' }} />
          </CardHeader>
          <CardContent>
            <div className="flex items-baseline gap-2">
              <div className="text-3xl font-bold text-green-600">
                {stats.activeStudents}
              </div>
              <span className="text-sm text-gray-600">Active</span>
            </div>
            <div className="flex items-baseline gap-2 mt-1">
              <div className="text-lg font-semibold text-gray-500">
                {stats.inactiveStudents}
              </div>
              <span className="text-xs text-gray-600">Inactive</span>
            </div>
          </CardContent>
        </Card>

        {/* Total Wardens */}
        <Card className="glass-effect card-hover" data-testid="total-wardens-card">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Wardens</CardTitle>
            <UserCheck className="h-5 w-5" style={{ color: '#5A9FD4' }} />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold" style={{ color: '#2C5F7F' }}>
              {stats.totalWardens}
            </div>
            <p className="text-xs text-gray-600 mt-1">
              Managing students
            </p>
          </CardContent>
        </Card>

        {/* Classes Summary */}
        <Card className="glass-effect card-hover" data-testid="classes-summary-card">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Classes</CardTitle>
            <BookOpen className="h-5 w-5" style={{ color: '#5A9FD4' }} />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold" style={{ color: '#2C5F7F' }}>
              {stats.classSummary.length}
            </div>
            <p className="text-xs text-gray-600 mt-1">
              Different classes
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Class-wise Student Count */}
      {stats.classSummary.length > 0 && (
        <Card className="glass-effect">
          <CardHeader>
            <CardTitle className="text-lg" style={{ color: '#2C5F7F' }}>
              Students by Class
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {stats.classSummary.map((classInfo, idx) => (
                <div
                  key={idx}
                  className="flex items-center justify-between p-3 rounded-lg border border-gray-200 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-center gap-3">
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center"
                      style={{ background: '#E8F4F9' }}
                    >
                      <BookOpen className="w-5 h-5" style={{ color: '#5A9FD4' }} />
                    </div>
                    <div>
                      <p className="font-semibold" style={{ color: '#2C5F7F' }}>
                        {classInfo.name}
                      </p>
                      <p className="text-xs text-gray-600">Class</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold" style={{ color: '#5A9FD4' }}>
                      {classInfo.count}
                    </p>
                    <p className="text-xs text-gray-600">Students</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick Actions */}
      <div className="glass-effect p-6 rounded-xl">
        <h2 className="text-xl font-semibold mb-4" style={{ color: '#2C5F7F' }}>
          Quick Actions
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <button
            onClick={() => navigate('/admin/students')}
            className="p-4 border-2 rounded-lg hover:shadow-md transition-all text-left"
            style={{ borderColor: '#5A9FD4' }}
          >
            <Users className="w-6 h-6 mb-2" style={{ color: '#5A9FD4' }} />
            <h3 className="font-semibold" style={{ color: '#2C5F7F' }}>
              Manage Students
            </h3>
            <p className="text-sm text-gray-600">View and edit students</p>
          </button>
          <button
            onClick={() => navigate('/admin/bulk-upload')}
            className="p-4 border-2 rounded-lg hover:shadow-md transition-all text-left"
            style={{ borderColor: '#5A9FD4' }}
          >
            <Upload className="w-6 h-6 mb-2" style={{ color: '#5A9FD4' }} />
            <h3 className="font-semibold" style={{ color: '#2C5F7F' }}>
              Bulk Import
            </h3>
            <p className="text-sm text-gray-600">Import from Excel</p>
          </button>
          <button
            onClick={() => navigate('/admin/wardens')}
            className="p-4 border-2 rounded-lg hover:shadow-md transition-all text-left"
            style={{ borderColor: '#5A9FD4' }}
          >
            <UserCheck className="w-6 h-6 mb-2" style={{ color: '#5A9FD4' }} />
            <h3 className="font-semibold" style={{ color: '#2C5F7F' }}>
              Manage Wardens
            </h3>
            <p className="text-sm text-gray-600">View and assign wardens</p>
          </button>
          <button
            onClick={() => navigate('/admin/settings')}
            className="p-4 border-2 rounded-lg hover:shadow-md transition-all text-left"
            style={{ borderColor: '#5A9FD4' }}
          >
            <Settings className="w-6 h-6 mb-2" style={{ color: '#5A9FD4' }} />
            <h3 className="font-semibold" style={{ color: '#2C5F7F' }}>
              Settings
            </h3>
            <p className="text-sm text-gray-600">Configure system</p>
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminHome;
